import { useState } from "react";
import { Link } from "react-router-dom";
import { artists, artistRewards } from "@/mocks/artistData";
import type { Reward } from "@/mocks/artistData";
import Navbar from "@/pages/home/components/Navbar";
import RewardFormModal from "@/pages/rewards/manage/components/RewardFormModal";

const categoryColors: Record<string, string> = {
  offline: "#3d9be9",
  credit: "#ffd60a",
  digital: "#ff6eb4",
  exclusive: "#c77dff",
};

const categoryLabels: Record<string, string> = {
  offline: "오프라인 이벤트",
  credit: "크레딧 등재",
  digital: "디지털 굿즈",
  exclusive: "독점 콘텐츠",
};

export default function RewardManagePage() {
  const [selectedArtistId, setSelectedArtistId] = useState("artist-001");
  const [showForm, setShowForm] = useState(false);
  const [editingReward, setEditingReward] = useState<Reward | null>(null);

  const artist = artists.find((a) => a.id === selectedArtistId);
  const myRewards = artistRewards.filter((r) => r.artistId === selectedArtistId);

  const totalStock = myRewards.reduce((sum, r) => sum + r.totalStock, 0);
  const redeemed = myRewards.reduce((sum, r) => sum + (r.totalStock - r.stock), 0);

  const handleEdit = (reward: Reward) => {
    setEditingReward(reward);
    setShowForm(true);
  };

  const handleNew = () => {
    setEditingReward(null);
    setShowForm(true);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />

      <div className="max-w-6xl mx-auto px-6 pt-28 pb-24">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-white/40 mb-8">
          <Link to="/rewards" className="hover:text-white/70 transition-colors cursor-pointer">리워드</Link>
          <i className="ri-arrow-right-s-line text-xs" />
          <span className="text-white/70">리워드 관리</span>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <div className="w-full lg:w-64 flex-shrink-0 space-y-4">
            <div>
              <h3 className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-3">크리에이터 선택</h3>
              <div className="space-y-2">
                {artists.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => setSelectedArtistId(a.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer text-left ${
                      selectedArtistId === a.id
                        ? "bg-white/10 border border-white/20"
                        : "hover:bg-white/5 border border-transparent"
                    }`}
                  >
                    <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={a.avatar} alt={a.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-white text-xs font-semibold truncate">{a.name}</div>
                      <div className="text-white/30 text-[10px] truncate">{a.categoryLabel}</div>
                    </div>
                    {selectedArtistId === a.id && (
                      <i className="ri-check-line text-xs ml-auto flex-shrink-0" style={{ color: a.accentColor }} />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                {artist && (
                  <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                    <img src={artist.avatar} alt={artist.name} className="w-full h-full object-cover" />
                  </div>
                )}
                <div>
                  <h1 className="text-white font-black text-xl">{artist?.name} 리워드 관리</h1>
                  <p className="text-white/40 text-sm">{artist?.tagline}</p>
                </div>
              </div>
              <button
                onClick={handleNew}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm text-[#0a0a0f] hover:opacity-90 transition-all cursor-pointer whitespace-nowrap"
                style={{ background: artist?.accentColor ?? "#ffd60a" }}
              >
                <i className="ri-add-line" />
                리워드 추가
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {[
                { label: "총 리워드", value: myRewards.length, icon: "ri-gift-line", color: "#ffd60a" },
                { label: "전체 수량", value: totalStock, icon: "ri-stack-line", color: "#39ff14" },
                { label: "교환 완료", value: redeemed, icon: "ri-exchange-line", color: "#4fc3f7" },
              ].map((s) => (
                <div key={s.label} className="p-5 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2 mb-2">
                    <i className={`${s.icon} text-sm`} style={{ color: s.color }} />
                    <span className="text-white/40 text-xs">{s.label}</span>
                  </div>
                  <div className="text-white font-black text-2xl">{s.value.toLocaleString()}</div>
                </div>
              ))}
            </div>

            {/* Reward List */}
            <div className="space-y-3">
              {myRewards.length === 0 ? (
                <div className="text-center py-20 text-white/30">
                  <i className="ri-gift-line text-4xl mb-4 block" />
                  <p className="text-sm">아직 리워드가 없습니다. 첫 번째 리워드를 추가해보세요!</p>
                </div>
              ) : (
                myRewards.map((reward) => {
                  const stockPct = Math.round((reward.stock / reward.totalStock) * 100);
                  const catColor = categoryColors[reward.category] ?? "#ffffff";
                  return (
                    <div
                      key={reward.id}
                      className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-white/20 transition-all"
                    >
                      <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0">
                        <img src={reward.image} alt={reward.title} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                            style={{ background: `${catColor}15`, color: catColor, border: `1px solid ${catColor}30` }}
                          >
                            {categoryLabels[reward.category]}
                          </span>
                          {reward.badge && (
                            <span className="text-[10px] text-white/40">{reward.badge}</span>
                          )}
                        </div>
                        <h4 className="text-white font-semibold text-sm truncate">{reward.title}</h4>
                        <div className="flex items-center gap-3 mt-1.5">
                          <div className="flex items-center gap-1">
                            <i className="ri-coin-fill text-[#ffd60a] text-xs" />
                            <span className="text-[#ffd60a] text-xs font-bold">{reward.xpCost.toLocaleString()} XP</span>
                          </div>
                          <div className="flex items-center gap-1.5 flex-1 max-w-32">
                            <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full"
                                style={{ width: `${stockPct}%`, background: stockPct > 30 ? catColor : "#f72585" }}
                              />
                            </div>
                            <span className="text-white/30 text-[10px] whitespace-nowrap">{reward.stock}/{reward.totalStock}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleEdit(reward)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 border border-white/10 text-white/50 hover:text-white hover:border-white/20 transition-all cursor-pointer"
                        >
                          <i className="ri-edit-line text-sm" />
                        </button>
                        <div
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-bold ${
                            reward.stock === 0
                              ? "bg-white/5 text-white/20"
                              : "bg-[#39ff14]/10 text-[#39ff14]"
                          }`}
                        >
                          {reward.stock === 0 ? <i className="ri-close-line" /> : <i className="ri-check-line" />}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {showForm && (
        <RewardFormModal
          reward={editingReward}
          artistId={selectedArtistId}
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  );
}
